import { isClientOption, isDefaultOption } from '@/helpers/other';
import type { ClientOption } from '@/types';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isUuid = (value: string) => UUID_REGEX.test(value);

const findMatchingClientOption = (
  option: Pick<ClientOption, 'value' | 'label' | 'source'>,
  previous: ClientOption[],
) =>
  previous.find(
    prev =>
      prev.value === option.value ||
      (prev.label === option.label && prev.source === option.source),
  );

const preserveClientOption = (
  option: ClientOption,
  previous: ClientOption[],
): ClientOption => {
  const match = findMatchingClientOption(option, previous);

  if (match && !isUuid(option.value) && isUuid(match.value)) {
    return match;
  }

  return option;
};

export const resolveClientOptionId = (option: ClientOption): string => {
  if (option.source === 'crunchbase') {
    return option.crunchbaseId ?? option.value;
  }

  return option.clientId ?? option.value;
};

export const hasResolvedClientOptionId = (option: ClientOption): boolean =>
  resolveClientOptionId(option).trim().length > 0;

export const mergeClientOptions = (
  next: unknown[],
  previous: ClientOption[],
): ClientOption[] => {
  return next
    .map(option => {
      if (isClientOption(option)) {
        return preserveClientOption(option, previous);
      }

      if (isDefaultOption(option)) {
        return previous.find(prev => prev.label === option.label) ?? null;
      }

      return null;
    })
    .filter(
      (option): option is ClientOption =>
        option !== null && isClientOption(option),
    );
};
